import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Button, Chip, IconButton, Tooltip,
  Snackbar, Alert, CircularProgress, Menu, MenuItem, Divider,
} from '@mui/material';
import {
  AssessmentOutlined as DreIcon,
  Print as PrintIcon,
  FileDownload as DownloadIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  AutoFixHigh as AutoClassifyIcon,
  AccountTree as ChartIcon,
  TrendingUp as KpiIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  dreService, DreSummaryRow, DreComputeRow, DreRegime, DreSummaryRowKind,
  formatBRL, formatPct,
} from '../../services/dreService';
import DreFilters from '../../components/dre/DreFilters';
import DreTable, { DreColumnMode } from '../../components/dre/DreTable';
import DreDrilldownDialog from '../../components/dre/DreDrilldownDialog';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

// =============================================================================
// KPI Card
// =============================================================================

interface KpiProps {
  label: string;
  value: number | null;
  positive?: boolean;
  loading?: boolean;
  helper?: string;
  highlight?: boolean;
  percent?: boolean;
}

const KpiCard: React.FC<KpiProps> = ({ label, value, positive, loading, helper, highlight, percent }) => {
  const fmt = percent ? formatPct(value, 1) : formatBRL(value);
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, borderRadius: 2, flex: 1, minWidth: 140,
        bgcolor: highlight ? '#0C6FC1' : '#fff',
        border: '1px solid',
        borderColor: highlight ? '#0a5da0' : '#e2e8f0',
        boxShadow: highlight ? '0 4px 16px rgba(12, 111, 193, 0.25)' : 'none',
      }}
    >
      <Typography sx={{
        fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1,
        color: highlight ? 'rgba(255,255,255,0.85)' : '#64748b',
        textTransform: 'uppercase', mb: 0.4,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.1,
        color: highlight ? '#fff' :
               value !== null && value < 0 ? '#dc2626' :
               positive ? '#16a34a' : '#0f172a',
        fontFamily: '"JetBrains Mono", monospace',
      }}>
        {loading ? '–' : fmt}
      </Typography>
      {helper && (
        <Typography sx={{
          fontSize: '0.65rem', mt: 0.3,
          color: highlight ? 'rgba(255,255,255,0.7)' : '#94a3b8',
        }}>
          {helper}
        </Typography>
      )}
    </Paper>
  );
};

// =============================================================================
// Página DRE
// =============================================================================

const Dre: React.FC = () => {
  const navigate = useNavigate();
  const { availableUnits, currentUnit } = useAuth();

  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [regime, setRegime] = useState<DreRegime>('cash');
  const [showAv, setShowAv] = useState(true);
  const [showAh, setShowAh] = useState(true);
  const [showYearTotal, setShowYearTotal] = useState(true);
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);

  const [summaryRows, setSummaryRows] = useState<DreSummaryRow[]>([]);
  const [detailRows, setDetailRows] = useState<DreComputeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  const omieInputRef = useRef<HTMLInputElement | null>(null);
  const [omieImportBusy, setOmieImportBusy] = useState(false);

  const [drilldown, setDrilldown] = useState<{
    open: boolean;
    rowKind?: DreSummaryRowKind;
    accountId?: string;
    accountIds?: string[];
    bucket?: string;
    rowLabel?: string;
  }>({ open: false });

  const [exportMenu, setExportMenu] = useState<HTMLElement | null>(null);

  const periodStart = `${year}-01-01`;
  const periodEnd = `${year}-12-31`;

  /** Alinha colunas da tabela ao regime (filtros “Visualizar”) */
  const tableColumnMode: DreColumnMode = regime === 'accrual' ? 'expected' : 'realized';

  // ----------------- Fetch ---------------------------------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const bundle = await dreService.computeBundle({
        periodStart, periodEnd,
        unitIds: selectedUnitIds.length > 0 ? selectedUnitIds : null,
        regime,
      });
      setSummaryRows(bundle.summary);
      setDetailRows(bundle.detail);
    } catch (e: any) {
      console.error(e);
      const ax = e?.response?.data;
      const msg = ax?.error
        ? [ax.error, ax.details].filter(Boolean).join(' — ')
        : e?.message || 'Erro ao carregar DRE';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd, selectedUnitIds, regime]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ----------------- KPIs do ano (somatórios) -------------------------------
  const yearKpis = useMemo(() => {
    const total = (kind: DreSummaryRowKind) =>
      summaryRows
        .filter((r) => r.row_kind === kind)
        .reduce((s, r) => s + (Number(r.value_for_regime) || 0), 0);

    const revenue = total('REVENUE_TOTAL');
    const cv = total('VARIABLE_COST');
    const mc = total('CONTRIBUTION_MARGIN');
    const df = total('FIXED_EXPENSE');
    const inv = total('INVESTMENT');
    const lo = total('OPERATING_PROFIT');
    const result = total('NET_RESULT');
    const cashRec = total('CASH_RECONCILE');

    const mcPct = revenue !== 0 ? (mc / revenue) * 100 : null;
    const margemLiquida = revenue !== 0 ? (result / revenue) * 100 : null;

    return { revenue, cv, mc, df, inv, lo, result, cashRec, mcPct, margemLiquida };
  }, [summaryRows]);

  // ----------------- Snapshot ------------------------------------------------
  const handleSaveSnapshot = async () => {
    try {
      await dreService.createSnapshot({
        unit_id: selectedUnitIds.length === 1 ? selectedUnitIds[0] : currentUnit?.id || null,
        unit_ids: selectedUnitIds,
        consolidated: selectedUnitIds.length !== 1,
        period_start: periodStart,
        period_end: periodEnd,
        period_label: String(year),
        period_granularity: 'yearly',
        regime,
        payload: { summary: summaryRows, details: detailRows },
      });
      setSnackbar({ open: true, message: `Snapshot ${year} salvo!`, severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: `Falha ao salvar snapshot: ${e?.message || ''}`, severity: 'error' });
    }
  };

  // ----------------- Auto-classify ------------------------------------------
  const handleAutoClassify = async () => {
    try {
      const result = await dreService.autoClassify(false);
      const total = result.reduce((s, r) => s + (r.classified || 0), 0);
      setSnackbar({ open: true, message: `${total} registro(s) classificado(s)`, severity: 'success' });
      fetchData();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Falha: ${e?.message || ''}`, severity: 'error' });
    }
  };

  // ----------------- Export CSV ---------------------------------------------
  const exportCsv = () => {
    const buckets = Array.from(new Set(summaryRows.map((r) => r.bucket))).sort();
    const monthLabel = (b: string) => new Date(b).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
    const header = ['Linha', ...buckets.flatMap((b) => [`${monthLabel(b)} R$`, `${monthLabel(b)} AV%`, `${monthLabel(b)} AH%`])];
    const kindOrder = Array.from(new Set(summaryRows.sort((a,b)=>a.row_order-b.row_order).map(r => r.row_kind)));
    const rowsCsv = kindOrder.map((k) => {
      const r0 = summaryRows.find((s) => s.row_kind === k);
      const cells: any[] = [r0?.row_label || k];
      for (const b of buckets) {
        const r = summaryRows.find((s) => s.row_kind === k && s.bucket === b);
        cells.push(r?.value_for_regime ?? '', r?.av_pct ?? '', r?.ah_pct ?? '');
      }
      return cells.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',');
    });
    const blob = new Blob(['\uFEFF' + [header.join(','), ...rowsCsv].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `dre-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
    setExportMenu(null);
  };

  const exportPrint = () => {
    window.print();
    setExportMenu(null);
  };

  const handleOmieXlsxChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const inp = e.target;
    const file = inp.files?.[0];
    if (!file) return;
    if (!currentUnit?.id) {
      setSnackbar({ open: true, message: 'Selecione uma unidade ativa para importar a planilha OMIE.', severity: 'error' });
      inp.value = '';
      return;
    }
    setOmieImportBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post<{ success?: boolean; data?: {
        ledger_kind?: string;
        imported?: number;
        skipped_duplicates?: number;
        skipped_insert_errors?: number;
      } }>(
        getApiUrl('/financial-module/import/omie-financas-xlsx'),
        fd,
        { timeout: 20 * 60 * 1000 },
      );
      const d = res.data?.data || {};
      const parts = [
        `OMIE (${d.ledger_kind || '?'})`,
        `${d.imported ?? 0} novo(s)`,
        `${d.skipped_duplicates ?? 0} duplicado(s) ignorado(s)`,
      ];
      if (d.skipped_insert_errors) parts.push(`${d.skipped_insert_errors} erro(s) de inserção`);
      setSnackbar({ open: true, message: parts.join(' · '), severity: 'success' });
      fetchData();
    } catch (err: any) {
      const ax = err?.response?.data;
      const msg = ax?.error || err?.message || 'Falha na importação OMIE';
      setSnackbar({ open: true, message: String(msg), severity: 'error' });
    } finally {
      inp.value = '';
      setOmieImportBusy(false);
    }
  };

  // ----------------- Cell click ---------------------------------------------
  const onCellClick = ({
    rowKind, accountId, accountIds, bucket,
  }: {
    rowKind?: DreSummaryRowKind;
    accountId?: string;
    accountIds?: string[];
    bucket: string;
  }) => {
    let label = '';
    if (rowKind) {
      const r = summaryRows.find((s) => s.row_kind === rowKind);
      label = r?.row_label || rowKind;
    } else {
      const firstId = accountIds?.length ? accountIds[0] : accountId;
      if (firstId) {
        const d = detailRows.find((r) => r.account_id === firstId);
        label = d ? `${d.account_code} · ${d.account_name}` : 'Conta';
      }
    }
    setDrilldown({
      open: true, rowKind, accountId, accountIds, bucket, rowLabel: label,
    });
  };

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
      gap: 1.5,
    }}>
      {/* ── Header ── */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: { xs: 1.5, md: 2 }, py: 1, borderRadius: 2, flexShrink: 0,
          bgcolor: '#fff', border: '1px solid #e2e8f0',
        }}
      >
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          bgcolor: '#0C6FC1', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <DreIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, lineHeight: 1 }}>
            Demonstração de Resultado
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }}>
            DRE Gerencial · {year}
          </Typography>
        </Box>

        <input
          ref={omieInputRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: 'none' }}
          onChange={handleOmieXlsxChosen}
        />

        <Tooltip title="Importar export Excel OMIE (Finanças → Contas a pagar ou receber). Vai para o financeiro e alimenta o DRE via categoria; duplicados são ignorados. Usa a unidade ativa.">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={omieImportBusy ? <CircularProgress size={14} /> : <UploadFileIcon />}
              disabled={loading || omieImportBusy}
              onClick={() => omieInputRef.current?.click()}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Importar OMIE
            </Button>
          </span>
        </Tooltip>

        <Tooltip title="Auto-classificar lançamentos legados">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoClassifyIcon />}
            onClick={handleAutoClassify}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Auto-classificar
          </Button>
        </Tooltip>

        <Tooltip title="Plano de contas">
          <Button
            size="small"
            variant="outlined"
            startIcon={<ChartIcon />}
            onClick={() => navigate('/financial/chart-of-accounts')}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Plano de Contas
          </Button>
        </Tooltip>

        <Tooltip title="Snapshots salvos">
          <Button
            size="small"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate('/financial/dre/snapshots')}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Histórico
          </Button>
        </Tooltip>

        <Tooltip title="Salvar snapshot">
          <span>
            <IconButton size="small" onClick={handleSaveSnapshot} disabled={loading || summaryRows.length === 0}>
              <SaveIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Button
          size="small"
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={(e) => setExportMenu(e.currentTarget)}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 2,
            bgcolor: '#0C6FC1', '&:hover': { bgcolor: '#0a5da0' },
          }}
        >
          Exportar
        </Button>
        <Menu anchorEl={exportMenu} open={!!exportMenu} onClose={() => setExportMenu(null)}>
          <MenuItem onClick={exportCsv}>
            <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
            CSV (planilha)
          </MenuItem>
          <MenuItem onClick={exportPrint}>
            <PrintIcon fontSize="small" sx={{ mr: 1 }} />
            Imprimir / PDF
          </MenuItem>
        </Menu>
      </Paper>

      {/* ── KPIs do ano ── */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', flexShrink: 0 }}>
        <KpiCard label="Receita" value={yearKpis.revenue} loading={loading} highlight />
        <KpiCard label="Custos Variáveis" value={-yearKpis.cv} loading={loading} />
        <KpiCard label="Margem de Contribuição" value={yearKpis.mc} loading={loading} positive helper={yearKpis.mcPct !== null ? `${formatPct(yearKpis.mcPct, 1)} da receita` : ''} />
        <KpiCard label="Despesas Fixas" value={-yearKpis.df} loading={loading} />
        <KpiCard label="Investimentos" value={-yearKpis.inv} loading={loading} />
        <KpiCard label="Lucro Operacional" value={yearKpis.lo} loading={loading} />
        <KpiCard label="Resultado Líquido" value={yearKpis.result} loading={loading} positive={yearKpis.result >= 0} helper={yearKpis.margemLiquida !== null ? `Margem ${formatPct(yearKpis.margemLiquida, 1)}` : ''} />
        <KpiCard label="Acerto do Caixa" value={yearKpis.cashRec} loading={loading} helper="diferença caixa físico" />
      </Box>

      {/* ── Filtros ── */}
      <DreFilters
        year={year}
        onYearChange={setYear}
        regime={regime}
        onRegimeChange={setRegime}
        showAv={showAv}
        showAh={showAh}
        showYearTotal={showYearTotal}
        onToggleAv={() => setShowAv((v) => !v)}
        onToggleAh={() => setShowAh((v) => !v)}
        onToggleYearTotal={() => setShowYearTotal((v) => !v)}
        onRefresh={fetchData}
        loading={loading}
        units={availableUnits || []}
        selectedUnitIds={selectedUnitIds}
        onUnitsChange={setSelectedUnitIds}
      />

      {/* ── Tabela ── */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
        {error && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}
        {!error && (
          <DreTable
            summaryRows={summaryRows}
            detailRows={detailRows}
            loading={loading}
            showAv={showAv}
            showAh={showAh}
            showYearTotal={showYearTotal}
            columnMode={tableColumnMode}
            onCellClick={onCellClick}
          />
        )}
      </Box>

      {/* ── Drilldown ── */}
      <DreDrilldownDialog
        open={drilldown.open}
        onClose={() => setDrilldown({ open: false })}
        rowKind={drilldown.rowKind}
        accountId={drilldown.accountId}
        accountIds={drilldown.accountIds}
        bucket={drilldown.bucket}
        rowLabel={drilldown.rowLabel}
        unitIds={selectedUnitIds.length > 0 ? selectedUnitIds : undefined}
        regime={regime}
      />

      {/* ── Snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dre;
